import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Plus, 
  Trash2,
  Edit2,
  AlertTriangle,
  Clock
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default absence types (used as fallback)
const DEFAULT_ABSENCE_TYPES = [
  { id: 'sick_leave', name: 'Baja médica', is_justified: true, allows_hours: false },
  { id: 'personal', name: 'Asuntos propios', is_justified: false, allows_hours: true },
  { id: 'other', name: 'Otro', is_justified: false, allows_hours: true }
];

const Absences = () => {
  const { isAdmin, isSupervisor } = useAuth();
  const { t, language } = useLanguage();
  const [absences, setAbsences] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [absenceTypes, setAbsenceTypes] = useState(DEFAULT_ABSENCE_TYPES);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  
  // Form
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    absence_type: 'sick_leave',
    custom_absence_type: '',
    start_date: '',
    end_date: '',
    days: 0,
    notes: '',
    is_justified: true,
    is_hourly: false,
    hours: 0,
    start_time: '',
    end_time: ''
  });

  useEffect(() => {
    fetchData();
  }, [typeFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (typeFilter && typeFilter !== 'all') params.absence_type = typeFilter;
      
      const [absencesRes, employeesRes, absenceTypesRes] = await Promise.all([
        axios.get(`${API_URL}/absences`, { params }),
        axios.get(`${API_URL}/employees`),
        axios.get(`${API_URL}/settings/absence-types`)
      ]);
      setAbsences(absencesRes.data);
      setAllEmployees(employeesRes.data);
      setEmployees(employeesRes.data.filter(e => e.is_active !== false));
      
      // Use configured absence types if available
      if (absenceTypesRes.data.absence_types && absenceTypesRes.data.absence_types.length > 0) {
        setAbsenceTypes(absenceTypesRes.data.absence_types);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get current selected absence type config
  const getCurrentAbsenceTypeConfig = () => {
    return absenceTypes.find(at => at.id === formData.absence_type) || null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        // Store the custom type name for display purposes
        custom_absence_type: getCurrentAbsenceTypeConfig()?.name || formData.absence_type
      };
      
      // If not hourly, clear hour fields
      if (!formData.is_hourly) {
        payload.is_hourly = false;
        payload.hours = null;
        payload.start_time = null;
        payload.end_time = null;
      }

      if (editingAbsence) {
        await axios.put(`${API_URL}/absences/${editingAbsence.id}`, payload);
        toast.success(language === 'es' ? 'Ausencia actualizada' : 'Absence updated');
      } else {
        await axios.post(`${API_URL}/absences`, payload);
        toast.success(language === 'es' ? 'Ausencia registrada' : 'Absence registered');
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
    }
  };

  const handleEdit = (absence) => {
    setEditingAbsence(absence);
    setFormData({
      employee_id: absence.employee_id,
      absence_type: absence.absence_type,
      custom_absence_type: absence.custom_absence_type || '',
      start_date: absence.start_date,
      end_date: absence.end_date,
      days: absence.days,
      notes: absence.notes || '',
      is_justified: absence.is_justified !== false,
      is_hourly: absence.is_hourly || false,
      hours: absence.hours || 0,
      start_time: absence.start_time || '',
      end_time: absence.end_time || ''
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAbsence(null);
    setFormData({ 
      employee_id: '', 
      absence_type: 'sick_leave',
      custom_absence_type: '',
      start_date: '', 
      end_date: '', 
      days: 0, 
      notes: '',
      is_justified: true,
      is_hourly: false,
      hours: 0,
      start_time: '',
      end_time: ''
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirm_delete'))) {
      try {
        await axios.delete(`${API_URL}/absences/${id}`);
        toast.success(language === 'es' ? 'Ausencia eliminada' : 'Absence deleted');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || t('error_occurred'));
      }
    }
  };

  // Calculate days or hours based on mode
  const calculateDaysOrHours = () => {
    if (formData.is_hourly && formData.start_time && formData.end_time) {
      // Calculate hours from start_time and end_time
      const [startH, startM] = formData.start_time.split(':').map(Number);
      const [endH, endM] = formData.end_time.split(':').map(Number);
      const startMinutes = startH * 60 + startM;
      const endMinutes = endH * 60 + endM;
      const diffMinutes = endMinutes - startMinutes;
      const hours = Math.max(0, diffMinutes / 60);
      setFormData(prev => ({ ...prev, hours: Math.round(hours * 100) / 100, days: Math.round(hours / 8 * 100) / 100 }));
    } else if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      setFormData(prev => ({ ...prev, days: Math.max(0, diff), hours: 0 }));
    }
  };

  useEffect(() => {
    calculateDaysOrHours();
  }, [formData.start_date, formData.end_date, formData.start_time, formData.end_time, formData.is_hourly]);

  // Update is_justified when absence type changes
  useEffect(() => {
    const config = getCurrentAbsenceTypeConfig();
    if (config) {
      setFormData(prev => ({ 
        ...prev, 
        is_justified: config.is_justified,
        // Reset hourly mode if type doesn't allow it
        is_hourly: config.allows_hours ? prev.is_hourly : false
      }));
    }
  }, [formData.absence_type]);

  const getEmployeeName = (id) => allEmployees.find(e => e.id === id)?.name || id;

  const getTypeBadge = (absence) => {
    // Try to find a matching configured type
    const typeName = absence.custom_absence_type || 
      absenceTypes.find(at => at.id === absence.absence_type)?.name || 
      absence.absence_type;
    
    // Color based on justified status
    const bgColor = absence.is_justified !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800';
    
    return (
      <div className="flex items-center gap-2">
        <Badge className={bgColor}>{typeName}</Badge>
        {absence.is_hourly && (
          <Badge variant="outline" className="text-xs">
            <Clock className="w-3 h-3 mr-1" />
            {absence.hours}h
          </Badge>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const currentTypeConfig = getCurrentAbsenceTypeConfig();
  const allowsHours = currentTypeConfig?.allows_hours ?? false;

  return (
    <div className="space-y-6" data-testid="absences-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900">{t('absences')}</h1>
          <p className="text-slate-500">
            {language === 'es' ? 'Registro de ausencias y bajas médicas' : 'Absences and sick leave records'}
          </p>
        </div>
        {(isAdmin() || isSupervisor()) && (
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700" 
            data-testid="register-absence-btn"
            onClick={() => {
              setEditingAbsence(null);
              const firstType = absenceTypes[0];
              setFormData({ 
                employee_id: '', 
                absence_type: firstType?.id || 'sick_leave',
                custom_absence_type: '',
                start_date: '', 
                end_date: '', 
                days: 0, 
                notes: '',
                is_justified: firstType?.is_justified ?? true,
                is_hourly: false,
                hours: 0,
                start_time: '',
                end_time: ''
              });
              setDialogOpen(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('register_absence')}
          </Button>
        )}
      </div>

      {/* Absence Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingAbsence 
                ? (language === 'es' ? 'Editar Ausencia' : 'Edit Absence')
                : t('register_absence')
              }
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Employee selector */}
            {!editingAbsence && (
              <div className="space-y-2">
                <Label>{t('employees')}</Label>
                <Select 
                  value={formData.employee_id} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, employee_id: value }))}
                >
                  <SelectTrigger data-testid="absence-employee-select">
                    <SelectValue placeholder={language === 'es' ? 'Seleccionar empleado' : 'Select employee'} />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {editingAbsence && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">{language === 'es' ? 'Empleado' : 'Employee'}</p>
                <p className="font-medium">{getEmployeeName(editingAbsence.employee_id)}</p>
              </div>
            )}

            {/* Absence type selector - using configured types */}
            <div className="space-y-2">
              <Label>{t('absence_type')}</Label>
              <Select 
                value={formData.absence_type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, absence_type: value }))}
              >
                <SelectTrigger data-testid="absence-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {absenceTypes.map(at => (
                    <SelectItem key={at.id} value={at.id}>
                      <div className="flex items-center gap-2">
                        <span>{at.name}</span>
                        {at.allows_hours && (
                          <Clock className="w-3 h-3 text-slate-400" />
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Hourly absence toggle - only show if type allows it */}
            {allowsHours && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <Label className="text-blue-900">{language === 'es' ? 'Ausencia por horas' : 'Hourly absence'}</Label>
                  <p className="text-xs text-blue-700">
                    {language === 'es' 
                      ? 'Registrar ausencia por horas en lugar de días completos'
                      : 'Register absence by hours instead of full days'}
                  </p>
                </div>
                <Switch
                  checked={formData.is_hourly}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_hourly: checked }))}
                  data-testid="absence-hourly-switch"
                />
              </div>
            )}

            {/* Date fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('start_date')}</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  required
                  data-testid="absence-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('end_date')}</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  required
                  data-testid="absence-end-date"
                />
              </div>
            </div>

            {/* Time fields - only show if hourly mode */}
            {formData.is_hourly && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="space-y-2">
                  <Label className="text-blue-900">{language === 'es' ? 'Hora inicio' : 'Start time'}</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                    required={formData.is_hourly}
                    data-testid="absence-start-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-blue-900">{language === 'es' ? 'Hora fin' : 'End time'}</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                    required={formData.is_hourly}
                    data-testid="absence-end-time"
                  />
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span className="text-sm text-slate-600">
                {formData.is_hourly ? (
                  <>
                    {language === 'es' ? 'Total horas' : 'Total hours'}: <strong className="text-slate-900">{formData.hours || 0}</strong>
                    <span className="text-slate-400 ml-2">
                      ({formData.days || 0} {language === 'es' ? 'días equiv.' : 'days equiv.'})
                    </span>
                  </>
                ) : (
                  <>
                    {language === 'es' ? 'Total días' : 'Total days'}: <strong className="text-slate-900">{formData.days}</strong>
                  </>
                )}
              </span>
            </div>

            {/* Justified toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label>{language === 'es' ? 'Ausencia justificada' : 'Justified absence'}</Label>
                <p className="text-xs text-slate-500">
                  {language === 'es' 
                    ? 'Las ausencias justificadas computan como horas trabajadas'
                    : 'Justified absences count as worked hours'}
                </p>
              </div>
              <Switch
                checked={formData.is_justified}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_justified: checked }))}
                data-testid="absence-justified-switch"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>{t('notes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={language === 'es' ? 'Notas adicionales...' : 'Additional notes...'}
                data-testid="absence-notes"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={handleCloseDialog}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="absence-submit-btn">
                {t('save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label>{t('absence_type')}</Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-48" data-testid="absence-type-filter">
                <SelectValue placeholder={language === 'es' ? 'Todos' : 'All'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'es' ? 'Todos' : 'All'}</SelectItem>
                {absenceTypes.map(at => (
                  <SelectItem key={at.id} value={at.id}>{at.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">{t('employees')}</TableHead>
                <TableHead className="font-semibold">{t('absence_type')}</TableHead>
                <TableHead className="font-semibold">{t('start_date')}</TableHead>
                <TableHead className="font-semibold">{t('end_date')}</TableHead>
                <TableHead className="font-semibold">{language === 'es' ? 'Duración' : 'Duration'}</TableHead>
                <TableHead className="font-semibold">{language === 'es' ? 'Justificada' : 'Justified'}</TableHead>
                <TableHead className="font-semibold">{t('notes')}</TableHead>
                {(isAdmin() || isSupervisor()) && <TableHead className="font-semibold text-right">{t('actions')}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {absences.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={(isAdmin() || isSupervisor()) ? 8 : 7} className="text-center py-8 text-slate-500">
                    {t('no_results')}
                  </TableCell>
                </TableRow>
              ) : (
                absences.map((absence) => (
                  <TableRow key={absence.id} className="hover:bg-slate-50" data-testid={`absence-row-${absence.id}`}>
                    <TableCell className="font-medium">{getEmployeeName(absence.employee_id)}</TableCell>
                    <TableCell>{getTypeBadge(absence)}</TableCell>
                    <TableCell className="text-slate-600">
                      {absence.start_date}
                      {absence.is_hourly && absence.start_time && (
                        <span className="text-xs text-blue-600 ml-1">{absence.start_time}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {absence.end_date}
                      {absence.is_hourly && absence.end_time && (
                        <span className="text-xs text-blue-600 ml-1">{absence.end_time}</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono tabular-nums">
                      {absence.is_hourly ? (
                        <span className="text-blue-600">{absence.hours}h</span>
                      ) : (
                        <span>{absence.days} {language === 'es' ? 'd' : 'd'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={absence.is_justified !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                        {absence.is_justified !== false ? (language === 'es' ? 'Sí' : 'Yes') : (language === 'es' ? 'No' : 'No')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500 max-w-xs truncate">{absence.notes || '-'}</TableCell>
                    {(isAdmin() || isSupervisor()) && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEdit(absence)}
                            data-testid={`edit-absence-${absence.id}`}
                            title={t('edit')}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(absence.id)}
                            data-testid={`delete-absence-${absence.id}`}
                            title={t('delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Absences;
